import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { StaffService } from '../../staff-service/staff.service';
import { AuthService } from 'src/app/service/auth-service/auth.service';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { takeUntil, catchError } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import * as moment from 'moment-timezone';
import * as _ from 'underscore';

@Component({
  selector: 'app-staff-timelines-dashboard',
  templateUrl: './staff-timelines-dashboard.component.html',
  styleUrls: ['./staff-timelines-dashboard.component.css']
})
export class StaffTimelinesDashboardComponent implements OnInit, OnDestroy {
  duration = 60 * 1000; // 60 seconds
  @ViewChild('timer', { static: true }) timer: any;
  view = 'list';
  timelineDetails: any[] = [];
  filteredTimeline: any[] = [];
  user: any;
  timeZone: string;
  scheduleList: any[] = [];
  breakList: any[] = [];
  timeoutList: any[] = [];
  totalMealBreaks = 0;
  totalRestBreaks = 0;
  search = '';
  roleOptions: any[] = [];
  selectedRoles: any[] = [];
  todayDate: any;
  breakDetail: boolean = false;
  timeline: any;
  timelineEvents: any[] = [];
  selectedStaff: any;
  staffsWithoutTimeline: any[] = [];
  filteredStaffsWithoutTimeline: any[] = [];
  staffByShift: any = {};
  staffList: any[] = [];

  constructor(
    private staffService: StaffService,
    private sharedDataService: SharedDataService,
    public authService: AuthService
  ) {}

  ngOnInit() {
    this.initRoles();
    this.getUser();
  }

  initRoles() {
    if (this.authService.staffComponentRoles) {
      this.roleOptions = Object.keys(this.authService.staffComponentRoles).map(key => ({
        key,
        value: this.authService.staffComponentRoles[key]
      }));
    }
  }

  getRoleTitle(roleKey: string): string {
    if (!roleKey) return 'Staff';
    if (this.authService.staffComponentRoles && this.authService.staffComponentRoles[roleKey]) {
      return this.authService.staffComponentRoles[roleKey];
    }
    return roleKey;
  }

  getUser() {
    this.sharedDataService
      .getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe((user) => {
        if (user) {
          this.user = user;
          this.timeZone = this.user.timeZone;
          this.todayDate = moment.tz(this.timeZone).startOf('day').toISOString();
          this.getStaffTimelines();
        }
      });
  }

  isBreakDueNow(breakObj: any): boolean {
    if (!breakObj) return true;
    if (breakObj.status === 'open') {
      return false; // Active open break
    } else if (['rest', 'approved'].includes(breakObj.status)) {
      if (!breakObj.stop) {
        return false;
      }
      const stop = moment(breakObj.stop).tz(this.timeZone, false);
      return moment.tz(this.timeZone).isAfter(stop);
    }
    return true;
  }

  getStaffTimelines() {
    this.staffService
      .getTimelinesByDate(this.todayDate)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          if (data && (data.timelines || data.staffsWithoutTimeline)) {
            this.processTimelinesData(data);
          } else {
            this.fallbackLoadTimelines();
          }
        },
        () => {
          this.fallbackLoadTimelines();
        }
      );
  }

  fallbackLoadTimelines() {
    const startOfDay = moment(this.todayDate).tz(this.timeZone).startOf('day').toISOString();
    const endOfDay = moment(this.todayDate).tz(this.timeZone).endOf('day').toISOString();

    forkJoin([
      this.staffService.getStaffList().pipe(catchError(() => of([]))),
      this.staffService.getAllStaffTimelines({ dates: { startDate: startOfDay, endDate: endOfDay } }).pipe(catchError(() => of([])))
    ])
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(([staffs, timelines]: [any[], any]) => {
        this.staffList = staffs || [];
        const timelinesArr = Array.isArray(timelines)
          ? timelines
          : (timelines && timelines.timelines ? timelines.timelines : []);

        const timelinesWithStaff = timelinesArr.map((t: any) => {
          if (!t.staff && t.staffId) {
            t.staff = this.staffList.find((s: any) => s._id === t.staffId);
          } else if (typeof t.staff === 'string') {
            const sObj = this.staffList.find((s: any) => s._id === t.staff);
            if (sObj) t.staff = sObj;
          }
          return t;
        });

        const staffIdsWithTimeline = new Set(
          timelinesWithStaff
            .map((t: any) => (t.staff ? t.staff._id : (t.staffId || (typeof t.staff === 'string' ? t.staff : null))))
            .filter(Boolean)
        );

        const staffsWithoutTimeline = this.staffList.filter((s: any) => !staffIdsWithTimeline.has(s._id));

        this.processTimelinesData({
          timelines: timelinesWithStaff,
          staffsWithoutTimeline: staffsWithoutTimeline
        });
      });
  }

  processTimelinesData(data: any) {
    if (this.timer && this.timer.start) {
      this.timer.start();
    }
    this.timelineDetails = data.timelines || [];
    this.timelineDetails.sort((a, b) => {
      const aCheckin = (a.shift && a.shift.checkin) || (a.staff && a.staff.shift && a.staff.shift.checkin) || '';
      const bCheckin = (b.shift && b.shift.checkin) || (b.staff && b.staff.shift && b.staff.shift.checkin) || '';
      return aCheckin.localeCompare(bCheckin);
    });
    this.filteredTimeline = this.timelineDetails;

    this.staffsWithoutTimeline = data.staffsWithoutTimeline || data.staffManifestWithoutTimeline || [];
    const now = moment(this.todayDate).tz(this.timeZone);

    this.staffsWithoutTimeline.forEach((staff: any) => {
      if (staff.shifts && staff.shifts.length) {
        this.staffByShift[staff._id] = staff.shifts.find((s: any) => s.isEnabled && s.day === now.day());
        if (this.staffByShift[staff._id] && this.staffByShift[staff._id].checkin) {
          const [h, m] = this.staffByShift[staff._id].checkin.split(':');
          const checkin = moment(this.todayDate).tz(this.timeZone).set({ hour: parseInt(h, 10), minute: parseInt(m, 10) });
          staff.shiftStartDiff = checkin.diff(now, 'minutes');
          staff.isCheckinTimePassed = staff.shiftStartDiff < 0;
        }
      }
    });

    this.staffsWithoutTimeline.sort((a, b) => (a.shiftStartDiff || 0) - (b.shiftStartDiff || 0));
    this.filteredStaffsWithoutTimeline = this.staffsWithoutTimeline;

    this.filteredData();
  }

  calculateBreaksTakenAndDue(breaks: any[]) {
    let mealTaken = 0;
    let restTaken = 0;
    let mealDue = 0;
    let restDue = 0;
    const now = moment().tz(this.timeZone);

    if (breaks && breaks.length) {
      breaks.forEach((b: any) => {
        const isRest = b.status === 'rest' || (b.label && b.label.toLowerCase().includes('rest'));
        const isMeal = b.status === 'open' || b.status === 'approved' || (b.label && b.label.toLowerCase().includes('meal'));

        if (b.start && b.stop) {
          const stopTime = moment(b.stop).tz(this.timeZone);
          if (stopTime.isAfter(now)) {
            if (isRest) restDue++;
            if (isMeal) mealDue++;
          } else {
            if (isRest) restTaken++;
            if (isMeal) mealTaken++;
          }
        } else if (b.start && !b.stop) {
          if (isMeal) mealDue++;
          if (isRest) restDue++;
        }
      });
    }

    return { mealTaken, restTaken, mealDue, restDue };
  }

  filteredData() {
    this.scheduleList = [];
    this.breakList = [];
    this.timeoutList = [];

    const now = moment().tz(this.timeZone);
    this.totalMealBreaks = 0;
    this.totalRestBreaks = 0;

    this.filteredTimeline.forEach((time: any) => {
      let breakTimeInMillis = 0;
      if (time.breaks && time.breaks.length) {
        time.breaks.forEach((b: any) => {
          const isMeal = b.status === 'approved' || b.status === 'open' || (b.label && b.label.toLowerCase().includes('meal'));
          const isRest = b.status === 'rest' || (b.label && b.label.toLowerCase().includes('rest'));

          if (isMeal) {
            this.totalMealBreaks++;
          }
          if (isRest) {
            this.totalRestBreaks++;
          }

          if (b.duration) {
            breakTimeInMillis += b.duration;
          } else if (b.start && b.stop) {
            breakTimeInMillis += moment(b.stop).diff(moment(b.start));
          }
        });
      }

      const breakStats = this.calculateBreaksTakenAndDue(time.breaks);
      time.mealTaken = breakStats.mealTaken;
      time.mealDue = breakStats.mealDue;
      time.restTaken = breakStats.restTaken;
      time.restDue = breakStats.restDue;

      if (time.status === 'completed' || (time.sessions && time.sessions.length && time.sessions[time.sessions.length - 1].checkout && time.checkout)) {
        time.breakTimeInMillis = breakTimeInMillis;
        this.timeoutList.push(time);
      } else {
        const checkIn = time.checkin ? moment(time.checkin).tz(this.timeZone, false) : now;
        const milliSeconds = now.diff(checkIn);
        time.workTimeInMillis = milliSeconds - breakTimeInMillis;
        if (time.workTimeInMillis < 0) {
          time.workTimeInMillis = 0;
        }
        time.breakTimeInMillis = breakTimeInMillis;

        if (time.breaks && time.breaks.length) {
          const latestBreak = time.breaks[time.breaks.length - 1];
          if (!this.isBreakDueNow(latestBreak)) {
            this.breakList.push(time);
          } else {
            this.scheduleList.push(time);
          }
        } else {
          this.scheduleList.push(time);
        }
      }
    });
  }

  viewHourFormat(milli: number): string {
    if (!milli || milli < 0) return '0hr 0mins';
    const duration = moment.duration(milli, 'milliseconds');
    return `${Math.floor(duration.asHours())}hr ${duration.minutes()}mins`;
  }

  onTimerComplete(): void {
    if (this.timer && this.timer.init) {
      this.timer.init();
    }
    this.getStaffTimelines();
  }

  setView(type: string) {
    this.view = type;
  }

  getStaffCount(): number {
    let count = 0;
    if (this.scheduleList && this.scheduleList.length) {
      count += this.scheduleList.length;
    }
    if (this.breakList && this.breakList.length) {
      count += this.breakList.length;
    }
    if (this.timeoutList && this.timeoutList.length) {
      count += this.timeoutList.length;
    }
    return count;
  }

  onDateChange(event: any) {
    this.todayDate = event.toISOString();
    this.getStaffTimelines();
  }

  filterStaff() {
    this.filteredTimeline = this.timelineDetails.filter((itemOne: any) => {
      let isRoleMatched = true;
      if (this.selectedRoles && this.selectedRoles.length) {
        const role = (itemOne.staff && itemOne.staff.roles) || (itemOne.staff && itemOne.staff.role);
        isRoleMatched = this.selectedRoles.includes(role);
      }
      return isRoleMatched;
    });

    this.filteredStaffsWithoutTimeline = this.staffsWithoutTimeline.filter((staff: any) => {
      let isRoleMatched = true;
      if (this.selectedRoles && this.selectedRoles.length) {
        const role = staff.roles || staff.role;
        isRoleMatched = this.selectedRoles.includes(role);
      }
      return isRoleMatched;
    });

    this.filteredData();
  }

  closeView() {
    this.breakDetail = false;
  }

  viewDetail(currentTimeline: any) {
    if (currentTimeline._id) {
      this.staffService
        .getStaffTimelineById(currentTimeline._id)
        .pipe(takeUntil(componentDestroyed(this)))
        .subscribe(
          (data) => {
            if (data) {
              this.timeline = data;
              this.buildTimelineEvents();
              const matched = this.timelineDetails.find((d: any) => d.staff && d.staff._id === this.timeline.staff);
              this.selectedStaff = currentTimeline.staff || (matched ? matched.staff : null);
              this.breakDetail = true;
            }
          },
          () => {
            this.timeline = currentTimeline;
            this.buildTimelineEvents();
            this.selectedStaff = currentTimeline.staff;
            this.breakDetail = true;
          }
        );
    } else {
      this.timeline = currentTimeline;
      this.buildTimelineEvents();
      this.selectedStaff = currentTimeline.staff;
      this.breakDetail = true;
    }
  }

  buildTimelineEvents() {
    if (!this.timeline) {
      this.timelineEvents = [];
      return;
    }

    const events: any[] = [];

    if (this.timeline.sessions && this.timeline.sessions.length) {
      this.timeline.sessions.forEach((session: any) => {
        if (session.checkin) {
          events.push({
            type: 'checkin',
            sortTime: session.checkin,
            data: session
          });
        }
        if (session.checkout) {
          events.push({
            type: 'checkout',
            sortTime: session.checkout,
            data: session
          });
        }
      });
    }

    if (this.timeline.breaks && this.timeline.breaks.length) {
      this.timeline.breaks.forEach((b: any) => {
        events.push({
          type: 'break',
          sortTime: b.start || b.updatedAt,
          data: b
        });
      });
    }

    events.sort((a, b) => new Date(a.sortTime).getTime() - new Date(b.sortTime).getTime());
    this.timelineEvents = events;
  }

  ngOnDestroy(): void {}
}
